import React, { forwardRef } from "../../__compat__/react/main.entry.mjs.mjs";
import { useSpacing } from "../../api/spacing/useSpacing.mjs.mjs";
import { pickAriaProps, pickComponentCommonProps, pickDataProps, } from "../../interfaces/commonProps.mjs.mjs";
import { FormHelperText } from "../FormHelperText/index.mjs.mjs";
import { StyledBadgeShape, StyledBadgeCount, StyledBadgeExclamationMark, StyledBadgeLayout, StyledBadgeLayoutBadgeShape, StyledBadgeLayoutLabel, } from "./Badge.styles.mjs.mjs";
let badgeShapeId = 0;
let labelId = 0;
function generateBadgeShapeId() {
    return `Badge-Shape-${badgeShapeId++}`;
}
function generateLabelId() {
    return `Badge-Label-${labelId++}`;
}
export const Badge = forwardRef(function Badge(props, ref) {
    const badgeState = useBadgeState(props);
    const commonRootElementProps = {
        ref,
        outerSpacing: useSpacing(props),
        ...pickComponentCommonProps(props),
        ...pickDataProps(props),
    };
    const badgeShapeElement = (React.createElement(StyledBadgeShape, { ...(badgeState.rootElementType === 'badge-shape'
            ? commonRootElementProps
            : { outerSpacing: [undefined] }), ...getLiveElementA11yProps(badgeState, props), variant: badgeState.variant, id: badgeState.badgeShapeId }, badgeState.badgeShapeContentType === 'count' ? (React.createElement(StyledBadgeCount, { count: badgeState.count }, badgeState.count > 99 ? '99+' : badgeState.count)) : badgeState.badgeShapeContentType === 'exclamation-mark' ? (React.createElement(StyledBadgeExclamationMark, { "aria-hidden": "true" }, "!")) : undefined));
    if (badgeState.rootElementType === 'badge-shape') {
        return badgeShapeElement;
    }
    return (React.createElement(StyledBadgeLayout, { ...commonRootElementProps, labelAlignment: badgeState.labelAlignment },
        React.createElement(StyledBadgeLayoutBadgeShape, null, badgeShapeElement),
        props.label && (React.createElement(StyledBadgeLayoutLabel, { badgeShapeSize: badgeState.badgeShapeSize, labelAlignment: badgeState.labelAlignment, id: badgeState.labelId },
            React.createElement(FormHelperText, null, props.label.text)))));
});
function useBadgeState({ variant: variantUserValue, count: countUserValue, label: labelUserValue, id: idUserValue, }) {
    var _a;
    const { current: generatedBadgeShapeId } = React.useRef(generateBadgeShapeId());
    const { current: generatedLabelId } = React.useRef(generateLabelId());
    const hasCount = countUserValue !== undefined && countUserValue > 0;
    const variant = hasCount
        ? 'informative'
        : variantUserValue !== null && variantUserValue !== void 0 ? variantUserValue : 'informative';
    const labelAlignment = (_a = labelUserValue === null || labelUserValue === void 0 ? void 0 : labelUserValue.align) !== null && _a !== void 0 ? _a : 'end';
    const hasLabel = Boolean(labelUserValue && labelUserValue.text);
    const labelId = generatedLabelId;
    const rootElementType = hasLabel ? 'badge-layout' : 'badge-shape';
    const badgeShapeSize = hasCount || variant === 'alert' ? 'large' : 'small';
    const badgeShapeId = rootElementType === 'badge-shape'
        ? idUserValue !== null && idUserValue !== void 0 ? idUserValue : generatedBadgeShapeId
        : generatedBadgeShapeId;
    const stateCommon = {
        variant,
        labelAlignment,
        labelId,
        badgeShapeSize,
        badgeShapeId,
        rootElementType,
    };
    if (hasCount) {
        return {
            ...stateCommon,
            badgeShapeContentType: 'count',
            count: countUserValue,
        };
    }
    return {
        ...stateCommon,
        badgeShapeContentType: variant === 'alert' ? 'exclamation-mark' : 'none',
    };
}
function getLiveElementA11yProps(state, props) {
    const { role: roleUserValue, 'aria-label': ariaLabelUserValue } = props;
    const { variant, badgeShapeContentType } = state;
    const hasCount = badgeShapeContentType == 'count';
    const role = (roleUserValue !== null && roleUserValue !== void 0 ? roleUserValue : hasCount)
        ? 'status'
        : variant === 'alert'
            ? 'alert'
            : undefined;
    const ariaLabel = ariaLabelUserValue;
    const ariaLabelledBy = getLiveElementAriaLabelledBy(state, props);
    return {
        ...pickAriaProps(props),
        role,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
    };
}
function getLiveElementAriaLabelledBy(state, props) {
    const { 'aria-labelledby': ariaLabelledByUserValue, 'aria-label': ariaLabelUserValue, } = props;
    const { rootElementType, badgeShapeId, labelId } = state;
    const hasLabel = rootElementType === 'badge-layout';
    if (ariaLabelUserValue || ariaLabelledByUserValue) {
        return ariaLabelledByUserValue !== null && ariaLabelledByUserValue !== void 0 ? ariaLabelledByUserValue : undefined;
    }
    if (hasLabel) {
        return [badgeShapeId, labelId].join(' ');
    }
    return undefined;
}
//# sourceMappingURL=Badge.mjs.mjs.map